import {Avatar, classNames, IconNames, layout, MaterialIcon, typography, useAsync, useFlag} from "@variocube/cube-ui";
import * as React from "react";
import {PropsWithChildren, useCallback, useEffect, useMemo, useState} from "react";
import {Controller, InternetConnection} from "./controller/Controller";
import {stylesheet} from "typestyle";
import {AwesomeBarDropdown} from "./AwesomeBarDropdown";
import {AwesomeBarItem} from "./AwesomeBarItem";
import {useLocalization} from "../i18n";
import {useCurrentEventValue} from "./EventSource";
import {unit} from "./controller/unit";
import {Instant, RelativeTemporalFormat} from "@variocube/app-ui";

interface SettingsProps {
    controller: Controller;
    onSettings: () => void;
}

export function SettingsItem({controller, onSettings}: SettingsProps) {
    const {t} = useLocalization();
    const [open, setOpen, clearOpen] = useFlag(false);

    const internetConnected = useInternetConnected(controller);

    const handleSettings = useCallback(() => {
        clearOpen();
        onSettings();
    }, [onSettings]);

    const unitStatus = useCurrentEventValue(unit.status);
    const unitConnected = useCurrentEventValue(unit.connected);

    return (
        <AwesomeBarDropdown
            open={open}
            onClose={clearOpen}
            button={
                <AwesomeBarItem className={styles.settingsIcon} onClick={setOpen}>
                    {MaterialIcon.settings}
                    {!internetConnected && (
                        <div className={styles.iconWarningOverlay}>
                            {MaterialIcon.warning}
                        </div>
                    )}
                </AwesomeBarItem>
            }
            alignContent="right"
        >
                <div className={classNames(layout.paddingMedium, styles.panel)}>
                    <div className={classNames(layout.paddingSmall, typography.alignCenter)}>
                        {t("ConnectionStatus")}
                    </div>
                    <div className={layout.horizontal}>
                        <ConnectivityItem
                            name={"Controller"}
                            icon={IconNames.developer_board}
                            status={Status.okay}
                            statusText={t("ControllerConnected")}
                        >
                            {controller.version && (
                                <div className={styles.version}>Version: {controller.version}</div>
                            )}
                            {controller.stage && (
                                <div className={styles.version}>Stage: {controller.stage}</div>
                            )}
                            {controller.region && (
                                <div className={styles.version}>Region: {controller.region}</div>
                            )}
                            <div className={styles.version}>Hostname: {controller.hostname}</div>
                        </ConnectivityItem>
                        <ConnectivityItem
                            name={"Unit"}
                            icon={IconNames.developer_board}
                            status={unitConnected ? Status.okay : Status.error}
                            statusText={t(unitConnected ? "ControllerConnected" : "ControllerDisconnected")}
                        >
                            {unitStatus?.id && (
                                <div className={styles.version}>Hostname: {unitStatus?.id}</div>
                            )}
                            {unitStatus?.boot && (
                                <div className={styles.version}>Boot: <RelativeTemporalFormat value={Instant.fromEpochMilliseconds(unitStatus.boot)} /></div>
                            )}
                            {(!unitStatus?.connected && unitStatus?.lastConnected) && (
                                <div className={styles.version}>Last connected: <RelativeTemporalFormat value={Instant.fromEpochMilliseconds(unitStatus.lastConnected)} /></div>
                            )}
                        </ConnectivityItem>
                        <ConnectivityItem
                            name={"Internet"}
                            icon={IconNames.router}
                            status={internetConnected ? Status.okay : Status.error}
                            statusText={internetConnected ? t("InternetConnected") : t("InternetDisconnected")}
                        />
                    </div>
                </div>
                <div className={classNames(layout.horizontal, layout.center, layout.alignCenter, styles.settingsItem)} onClick={handleSettings}>
                    <div className={styles.settingsItemIcon}>
                        {MaterialIcon.settings}
                    </div>
                    <div>
                        {t("Settings")}
                    </div>
                </div>
        </AwesomeBarDropdown>
    );

}

function useInternetConnected(controller: Controller) {
    const [internetConnected, setInternetConnected] = useState(false);

    const handleInternetConnectionChange = useCallback((connection: InternetConnection) => {
        setInternetConnected(connection.connected);
    }, []);

    useEffect(() => {
        controller.addInternetConnectionListener(handleInternetConnectionChange);
        return () => controller.removeInternetConnectionListener(handleInternetConnectionChange);
    }, [controller]);

    const {result} = useAsync(() => controller.getInternetConnection(), [controller]);
    useEffect(() => {
        if (result) {
            setInternetConnected(result.connected);
        }
    });

    return internetConnected;
}

enum Status {
    error = "error",
    warning = "warning",
    okay = "okay",
}

interface ConnectivityItemProps {
    name: string;
    status: Status;
    statusText: string;
    icon: IconNames;
}

function ConnectivityItem({name, status, statusText, icon, children}: PropsWithChildren<ConnectivityItemProps>) {

    const statusClass = useMemo(() => {
        switch (status) {
            case Status.okay:
            case Status.warning:
                return typography.colorSuccess;
            case Status.error:
                return typography.colorError;
        }
    }, [status]);

    return (
        <div className={styles.item}>
            <div className={styles.avatarContainer}>
                <Avatar
                    size={2}
                    success={status == Status.okay}
                    warning={status == Status.warning}
                    error={status == Status.error}
                >
                    <MaterialIcon icon={icon}/>
                </Avatar>
            </div>
            <div>
                {name}
            </div>
            <div className={classNames(styles.itemStatus, statusClass)}>
                {statusText}
            </div>
            <div className={styles.itemDetails}>
                {children}
            </div>
        </div>
    );
}

const styles = stylesheet({
    settingsIcon: {
        fontSize: "1.5rem",
    },
    iconWarningOverlay: {
        position: "absolute",
        bottom: "15%",
        right: "10%",
        fontSize: "0.66em",
        color: "var(--color-warning)",
    },
    avatarContainer: {
        margin: "0 auto",
        width: "min-content",
        padding: "1em 0",
    },
    version: {
        whiteSpace: "nowrap",
    },
    item: {
        flex: 1,
        padding: "0 1.5em",
        textAlign: "center",
    },
    itemIcon: {
        margin: "0.75em auto",
        borderRadius: "50%",
        width: "3em",
        height: "3em",
        backgroundColor: "gray",
        color: "#fff",
    },
    itemStatus: {
        margin: "0.25em 0",
        textTransform: "uppercase",
        fontSize: "0.8em",
    },
    itemDetails: {
        fontSize: "0.8em",
        fontStyle: "italic"
    },
    panel: {
        background: "#222",
    },
    settingsItem: {
        background: "#222",
        color: "#fff",
        borderTop: "1px solid #666",
        padding: "0.75em"
    },
    settingsItemIcon: {
        fontSize: "1.25em",
        marginRight: "0.25em"
    }
})
